<template>
  <v-select
    :value="selectValue"
    :placeholder="placeholder"
    :clearable="clearable"
    :loading="loading"
    :options="selectOptions"
    :components="{OpenIndicator}"
    :searchable="searchable"
    :label="label"
    :selectable="option => !option.disabled"
    class="vs--custom-styles"
    :class="{
      'vs--custom-styles__searchable': searchable,
      'vs--custom-styles__bolder': selectedBolder,
      'vs--custom-styles__width-250': dropdownWidth === 250,
      'vs--custom-styles__width-300': dropdownWidth === 300,
      'vs--custom-styles__width-350': dropdownWidth === 350,
      'vs--custom-styles__width-400': dropdownWidth === 400,
      'vs--custom-styles__width-450': dropdownWidth === 450,
      'vs--custom-styles__width-500': dropdownWidth === 500,
      'vs--custom-styles__height-150': dropdownHeight === 150,
      'vs--custom-styles__height-200': dropdownHeight === 200,
      'vs--custom-styles__height-250': dropdownHeight === 250,
    }"
    :append-to-body="topDropdown"
    :calculate-position="topDropdown ? withPopper : undefined"
    @search:focus="focused = true"
    @search:blur="focused = false"
    @search="handleSearch"
    @input="handleChange"
  >
    <template
      v-for="(_, name) in $scopedSlots"
      :slot="name"
      slot-scope="slotData"
    >
      <slot :name="name" v-bind="slotData" />
    </template>
    <template v-if="searchable" #header>
      <icon
        name="search"
        :class="[
          $style.icon,
          { [$style.visible]: focused && !dataLoading },
        ]"
      />
    </template>
    <template v-if="createLabel" #list-header>
      <li
        role="button"
        :class="$style.create"
        @click="$emit('create')"
      >
        <icon
          name="plus"
          :class="$style.plus"
        />
        {{ createLabel }}
      </li>
    </template>
    <span slot="no-options">
      {{ hint }}
    </span>
  </v-select>
</template>

<script>
import debounce from 'lodash/debounce';
import VSelect from 'vue-select';
import { createPopper } from '@popperjs/core';
import 'vue-select/dist/vue-select.css';
import Icon from '@/ui-kit/Icon';

export default {
  name: 'SelectInput',
  components: {
    VSelect,
    Icon,
  },
  props: {
    value: {
      type: [String, Object, Number],
      default: undefined,
    },
    options: {
      type: Array,
      default: undefined,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    createLabel: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: 'title',
    },
    /**
     * Функция для подгрузки списка элементов
     */
    fetchData: {
      type: Function,
      default: undefined,
    },
    /**
     * Минимальное кол-во символов для начала поиска с помощью fetchData
     */
    minSearchLength: {
      type: Number,
      default: 3,
    },
    /**
     * Привязывать к v-model id элемента списка
     */
    bindValueId: {
      type: Boolean,
      default: false,
    },
    /**
     * Автоматический выбор единственного активного элемента в списке
     */
    autosetSingleOption: {
      type: Boolean,
      default: true,
    },
    /**
     * Возможность осуществления поиска по вариантам
     */
    searchable: {
      type: Boolean,
      default: true,
    },
    /**
     * Располагать выпадающий список сверху
     */
    topDropdown: {
      type: Boolean,
      default: false,
    },
    /**
     * Ширина выпадающего списка 250, 300, 350, 400, 450, 500
     */
    dropdownWidth: {
      type: Number,
      default: undefined,
    },
    /**
     * Высота выпадающего списка 150, 200, 250
     */
    dropdownHeight: {
      type: Number,
      default: undefined,
    },
    selectedBolder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focused: false,
      dataLoading: false,
      OpenIndicator: {
        render: createElement => createElement('span', { class: {'vs__custom-toggle': true} }),
      },
      fetchedOptions: [],
      text: '',
    };
  },
  computed: {
    selectValue() {
      if (this.bindValueId) {
        return this.selectOptions.find(({ id }) => id === this.value);
      }
      return this.value;
    },
    selectOptions() {
      return (this.fetchData ? this.fetchedOptions : this.options) ?? [];
    },
    hint() {
      return this.fetchData && this.text.length < this.minSearchLength ?
        this.$t('hint.start-typing') :
        this.$t('hint.nothing-found');
    },
  },
  watch: {
    selectOptions: function (selectOptions) {
      if (this.autosetSingleOption && selectOptions.length === 1) {
        this.handleChange(selectOptions[0]);
      }
    },
    value(value) {
      if (!value && this.fetchedOptions) {
        this.fetchedOptions = [];
      }
    },
  },
  methods: {
    handleChange(value) {
      if (value === null) {
        this.$emit('input', undefined);
        this.fetchedOptions = [];
        return;
      }
      this.$emit('input', this.bindValueId ? value.id : value);
    },
    handleSearch: function (text = '', loading) {
      this.text = text;
      if (!this.fetchData || text.length < this.minSearchLength) {
        this.fetchedOptions = this.selectValue ? [this.selectValue] : [];
        return;
      }
      this.dataLoading = true;
      loading(true);
      this.search(loading, text, this);
    },
    search: debounce(async (loading, text, vm) => {
      try {
        const result = await vm.fetchData(text);
        vm.fetchedOptions = result;
      } catch (e) {
        throw new Error(e);
      } finally {
        loading(false);
        vm.dataLoading = false;
      }
    }, 350),
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width;

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'top',
        modifiers: [
          {
            name: 'offset', options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          }]
      });

      return () => popper.destroy();
    }
  }
}
</script>

<style lang="scss" module>
.icon {
  position: absolute;
  right: 22px;
  top: 10px;
  pointer-events: none;
  opacity: 0;
  transition: opacity .3s ease-in-out;

  &.visible {
    opacity: 0.5;
  }
}

.create {
  font-size: 15px;
  position: relative;
  cursor: pointer;
  padding: 16px 24px;
}

.plus {
  position: absolute;
  right: 24px;
  top: 18px;
}
</style>

<style lang="scss">
@import '~@/ui-kit/_theme/main.scss';

.vs--custom-styles {
  .vs__search::placeholder {
    color: $text-secondary-color;
  }

  .vs__custom-toggle {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6.9px 4px 0 4px;
    border-color: #313A46 transparent transparent transparent;
    position: relative;
    top: 1px;
  }

  .vs__dropdown-toggle {
    background: #eef2f7;
    height: 38px;
    border: 1px solid #eef2f7;
    padding-left: 4px;
    padding-right: 1px;
    border-radius: 3px;
  }

  .vs__spinner {
    position: relative;
    left: -10px;
  }

  .vs__no-options {
    text-align: left;
    color: #313A46;
    opacity: 0.8;
    padding-left: 16px;
  }

  &.vs--open {
    .vs__dropdown-toggle {
      border-color: #C2D3EA;
    }

    .vs__selected {
      width: calc(100% - 4px);
    }
  }

  .vs__selected {
    position: relative;
    top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }

  .vs__selected-options {
    width: calc(100% - 36px);
  }

  .vs__clear {
    position: relative;
    top: 1px;

    path {
      fill: $text-secondary-color;
    }
  }

  &.vs--custom-styles__searchable {
    &.vs--open {
      .vs__selected {
        width: calc(100% - 36px);
      }
    }
  }

  &.vs--custom-styles__bolder {
    .vs__selected {
      font-weight: $font-weight-bold;
    }
  }

  [placeholder]{
    text-overflow: ellipsis;
  }

  &.vs--custom-styles__width-250 {
    .vs__dropdown-menu {
      width: 250px;
    }
  }

  &.vs--custom-styles__width-300 {
    .vs__dropdown-menu {
      width: 300px;
    }
  }

  &.vs--custom-styles__width-350 {
    .vs__dropdown-menu {
      width: 350px;
    }
  }

  &.vs--custom-styles__width-400 {
    .vs__dropdown-menu {
      width: 400px;
    }
  }

  &.vs--custom-styles__width-450 {
    .vs__dropdown-menu {
      width: 450px;
    }
  }

  &.vs--custom-styles__width-500 {
    .vs__dropdown-menu {
      width: 500px;
    }
  }

  &.vs--custom-styles__height-150 {
    .vs__dropdown-menu {
      max-height: 150px;
    }
  }

  &.vs--custom-styles__height-200 {
    .vs__dropdown-menu {
      max-height: 200px;
    }
  }

  &.vs--custom-styles__height-250 {
    .vs__dropdown-menu {
      max-height: 250px;
    }
  }
}

.vs__dropdown-menu {
  .vs__dropdown-option {
    padding: 16px 24px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }

  .vs__dropdown-option--highlight {
    background-color: #EEF2F7;
    color: #313A46;
  }

  .vs__dropdown-option--selected {
    font-weight: 500;
  }
}
</style>
